/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Box, Button, Card, CardActionArea, CardContent, Paper, Stack, TextField, Typography } from "@mui/material";
import { customScrollbarStyle } from "../../../utils/Functions";
import { Effect, InventoryItem, InventoryItemWithAvailable, Nullable, Setter } from "../../../utils/Interfaces";
import { NavigateBefore, Start } from "@mui/icons-material";

export interface InventoryEgressModalProps {
    inventories: InventoryItem[];
    setSelectedInventory: Setter<Nullable<InventoryItem>>;
    progressDialog: Effect;
    regressDialog: Effect;
}

function InventorySelector(props: InventoryEgressModalProps) {
    const { inventories, setSelectedInventory, progressDialog, regressDialog } = props;

    const [filter, setFilter] = useState<string>("");

    function filteredInventories(): InventoryItem[] {
        return inventories.filter((inventory: InventoryItem) => {
            if (filter.trim() === "") {
                return true;
            }

            const processedFilter = filter.toLowerCase();
            return (
                inventory.material.name.toLowerCase().includes(processedFilter) ||
                inventory.material.manufacturer?.toLowerCase().includes(processedFilter) ||
                inventory.material.part_number.toLowerCase().includes(processedFilter) ||
                inventory.material.notes?.toLowerCase().includes(processedFilter)
            );
        });
    }

    const filteredItemsStore: InventoryItem[] = filteredInventories();

    return (
        <React.Fragment>
            <Box sx={{ mb: 2 }} />

            <TextField
                fullWidth
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                label="Filter Materials"
                name="filter"
                size="small"
                sx={{ mb: 2, mt: 1 }}
            />

            <Box
                sx={{
                    maxHeight: "calc(50vh)",
                    minHeight: "calc(50vh)",
                    overflowY: "auto",
                    overflowX: "hidden",
                    ...customScrollbarStyle(),
                }}
            >
                {filteredItemsStore.length > 50 && (
                    <Typography variant="subtitle2" textAlign="center" sx={{ mb: 2 }}>
                        Only the first 50 materials are displayed, filter to refine your search.
                    </Typography>
                )}
                {filteredItemsStore.slice(0, 50).map((inventory: InventoryItem, index: number) => {
                    return (
                        <Card
                            key={index}
                            component={Paper}
                            sx={{
                                mb: 1,
                                mt: 1,
                                mr: 1,
                                opacity: (inventory as InventoryItemWithAvailable).available === 0 ? 0.35 : 1.0,
                            }}
                            onClick={() => {
                                if ((inventory as InventoryItemWithAvailable).available === 0) {
                                    return;
                                }
                                setSelectedInventory(inventory);
                                progressDialog();
                            }}
                        >
                            <CardActionArea disabled={(inventory as any).available === 0}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                        <Typography>{inventory.material.name}</Typography>
                                        <Start />
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="subtitle2">{inventory.material.part_number}</Typography>
                                        <Typography variant="subtitle2">{(inventory as any).available} Available</Typography>
                                    </Stack>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    );
                })}
            </Box>

            <Button onClick={regressDialog} sx={{ mt: 2, mb: -2 }}>
                <NavigateBefore />
                &nbsp;Prev
            </Button>
        </React.Fragment>
    );
}

export default InventorySelector;
